/* The builder fills the work column edge to edge — v3's `.main`.
 *
 * Unlike the library page it takes no `--app-width` and no gutter: the week is a seven-column grid
 * whose whole value is width, and centring it in a 1520px column on a wide monitor would throw
 * away the space the canvas exists to use. */

.page {
  display: flex;

  /* `flex: none`, NOT `flex: 1`, and the difference is the whole fix. `flex: 1` expands to
   * `flex-basis: 0%`, which REPLACES `height` as the flex item's main size — so the declared
   * `100dvh` below was ignored, the item grew to fill whatever `.work` was, and `.work` in turn
   * sized to this element's content. The page ended up 1138px tall in a 1000px viewport with the
   * legend bar below the fold. Measured in the browser, not reasoned about. */
  flex: none;
  flex-direction: column;
  min-width: 0;

  /* The chrome — top bar, phase band, context row, legend — is fixed; the canvas takes the rest
   * and scrolls inside itself, so the week header never leaves the screen.
   *
   * `100dvh` rather than `100%`, and this was measured rather than assumed. `AppShell` sizes
   * itself with `min-height: 100dvh`, which is right for a document-shaped screen like the plan
   * library — but a `min-height` is not a DEFINITE height, so a percentage resolves against
   * nothing and falls back to `auto`. The page then grew to its content, the canvas never reached
   * its `overflow: auto`, and the legend bar sat below the fold with the day cards clipped at the
   * viewport edge.
   *
   * The builder is an application surface rather than a document: its chrome stays put and its
   * canvas scrolls. Taking the viewport directly is what expresses that, and it lines up exactly
   * with the shell's own floor so no second scrollbar appears. */
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  background: var(--bg);

  /* The canon's leading, declared ONCE for the whole surface — see `--lh-auto` in `typography.css`
   * for what it is and why it matters here. The 02.08 gallery's specimen root sets a family and a
   * size and nothing else, so every label inside it inherits the browser's normal leading; `body`
   * sets 1.5, which is right for prose and wrong for a tool, and the difference accumulated to 26px
   * down one card. Anything on this screen that needs its own leading already declares one
   * (`--lh-display` on the session title, `--lh-exercise` on an exercise name, `--lh-none` on the ⋯),
   * and `BuilderNotice` restores `--lh-relaxed` for the one place there is real prose. */
  line-height: var(--lh-auto);
}

/* Programmed work that no weekday column could hold: a day with no `dayOfWeek`, or a second day
 * claiming one already taken. Warn-coloured because it is a data problem the trainer should see,
 * not an error in this app — and above the canvas, because it is about what the canvas is NOT
 * showing.
 *
 * It sits OUTSIDE the pane, which is a fix rather than a layout preference: inside it, the banner
 * scrolled out of view on exactly the weeks tall enough to hide a card. */
.overflow {
  flex: none;
  padding: var(--space-2) var(--space-14px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--warn-ink);
  background: var(--warn-soft);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The live region is ALWAYS in the document — that is what makes the next phase's banner reliably
 * announced — so "no overflow" is styled away rather than unmounted.
 *
 * NOT `display: none`, and this is the one rule in the file worth stopping on: `display: none`
 * removes the element from the accessibility tree, which is indistinguishable from never having
 * rendered it and reintroduces the defect. Stripping the box instead leaves an empty `<p>`, which
 * is 0px tall and 0px of border on its own, present and silent. */
.overflowEmpty {
  padding: 0;
  background: none;
  border-bottom: 0;
}

/* The refused-save banner. The words are the whole message, so nothing here is carried by hue, and
 * it keeps the same always-rendered / styled-away pair as the overflow banner above.
 *
 * ── `--danger-text-strong`, not `--danger-ink` ───────────────────────────────────────────────
 * `--danger-ink` is the ink for a WHITE card, which this is not: the banner sits on `--danger-soft`,
 * a 10% tint of the bright red, and over the light canvas that composites to #eeddd8. #d6453c
 * measures **3.34:1** there — below AA for `--fs-caption` at `--fw-semibold`, which is the smallest
 * text in this app that has to be read. `--danger-text-strong` (#b32218) measures **5.04:1** on the
 * same surface. The ramp's own comment in `colors.css` already prescribes exactly this pairing —
 * "text on the warm canvas or on the soft danger tint … `--danger-ink` measures 3.32 on that tint" —
 * so this is a token used as documented rather than a new decision. In dark both collapse onto the
 * bright red, which measures 5.81:1 on the tint over #0b0b0c.
 *
 * ── The height is NO LONGER reserved, and that reversal is deliberate ────────────────────────
 * This row used to hold `min-height: var(--h-save-banner)` whether or not it had anything to say, so
 * that a refused save could not insert 33.5px between the top bar and the canvas and move the week
 * down under a pointer that had just left Save.
 *
 * The 02.08 KANON gallery draws no such row — its phase band sits directly under its top bar — and
 * an always-empty 34px band of canvas between the two is a permanent, visible departure from it on
 * every render. Weighed against a one-time shift that happens only after an explicit Save that the
 * server refused, with the pointer on a button that does not move, the permanent cost is the larger
 * one. The height goes; the row keeps its `flex: none` and appears only when it speaks.
 *
 * What does NOT change is that the element is always in the document. `role="alert"` announces a
 * change to a region that already existed; a region inserted together with its text is not reliably
 * announced. An empty `<p>` with its box stripped is 0px tall, present and silent — which is exactly
 * what `.saveErrorEmpty` below now produces. */
.saveError {
  display: flex;
  flex: none;
  align-items: center;
  padding: var(--space-2) var(--space-14px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
  background: var(--danger-soft);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* Every part of the box goes — padding, tint and rule — so an empty banner occupies nothing at all
 * and the phase band sits directly under the top bar, as the canon draws it. NOT `display: none`,
 * which would take the region out of the accessibility tree and undo the point of rendering it. */
.saveErrorEmpty {
  padding: 0;
  background: none;
  border-bottom: 0;
}

/* `--text`, not `--accent-text`. This is the ONLY control in a notice — a trainer who cannot open
 * the plan has this link and the browser's Back — so it must not depend on hue to be found:
 * `--accent-text` measures 3.27:1 on the canvas, below AA for text at any size this app draws it
 * (the same measurement that took the accent off the roster's task count — see
 * `pages/clients/AGENTS.md`). The underline is permanent for the other half of the same rule: a
 * link distinguished from body copy by colour alone fails WCAG 1.4.1, and here there is no
 * surrounding interface to infer it from. */
.back {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-decoration: underline;
}

/* Ink, not ember: this ring lands on the notice's canvas, where the accent measures 2.62:1 —
 * the same surface-dependent failure the pane's own ring documents. */
.back:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1);
  border-radius: var(--radius-hair);
}

/* The phase band's own live region: what the last phase act DID, announced and never drawn.
 *
 * Visually hidden rather than styled away like the two banners above, because the band beside it
 * already shows the outcome — a visible notice would report something the trainer can see two rows
 * down. Clipped rather than `display: none`, which takes the element out of the accessibility tree
 * and turns the region back into one that did not exist when its text arrived.
 *
 * `position: absolute` so a 1px box is not a flex row of the page either. */
.announce {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
